import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  previewRotation,
  validateDefinition,
  weekStartFor,
  type IsoWeekday,
  type TaskDefinitionInput,
} from '@home-tasks/shared'
import {
  useActivateTask,
  useArchiveTask,
  useSaveTask,
  useSettings,
  useTasks,
  useUsers,
} from '../api/hooks.ts'
import { ActivateDialog } from '../components/ActivateDialog.tsx'
import { Button, Card, ErrorNote, Screen, SectionTitle, Spinner } from '../components/ui.tsx'
import { DAY_INITIALS, eur, shortDate, weekdayInitials } from '../lib/format.ts'
import type { TaskDefinition } from '../api/types.ts'

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

export function TasksScreen() {
  const tasks = useTasks()
  const users = useUsers()

  if (tasks.isLoading) return <Spinner />
  if (tasks.error) return <Screen><ErrorNote error={tasks.error} /></Screen>

  const nameOf = (id: string | null) => users.data?.find((u) => u.id === id)?.name ?? '—'
  const list = tasks.data ?? []
  const deductible = list.filter((t) => t.kind === 'DEDUCTIBLE')
  const bonus = list.filter((t) => t.kind === 'BONUS')

  function describe(task: TaskDefinition): string {
    const who =
      task.assignmentMode === 'FIXED'
        ? task.assignees.map((a) => nameOf(a.userId)).join(' y ') || '—'
        : task.assignmentMode === 'ROTATING'
          ? `rotativa (${task.rotationMembers.length})`
          : 'abierta'
    const when =
      task.recurrence === 'ONE_OFF'
        ? task.oneOffDate
          ? shortDate(task.oneOffDate)
          : 'puntual'
        : task.recurrence === 'WEEKLY_ANY'
          ? 'cualquier día'
          : task.recurrence === 'ON_DEMAND'
            ? 'a demanda'
            : weekdayInitials(task.daysOfWeek)
    return `${who} · ${when}`
  }

  return (
    <Screen>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Tareas</h1>
        <Link to="/tareas/nueva">
          <Button>Nueva</Button>
        </Link>
      </div>

      <SectionTitle>Obligatorias ({deductible.length})</SectionTitle>
      <Card>
        {deductible.length === 0 && (
          <p className="py-2 text-sm text-[var(--color-muted)]">Todavía no hay ninguna.</p>
        )}
        {deductible.map((task) => (
          <Link
            key={task.id}
            to={`/tareas/${task.id}`}
            className="flex items-center justify-between border-b border-[var(--color-line)] py-3 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="text-[15px] break-words">{task.title}</p>
              <p className="text-xs text-[var(--color-muted)]">{describe(task)}</p>
            </div>
            <span className="shrink-0 text-xs text-[var(--color-muted)]">
              {task.weight > 1 ? `peso ${task.weight}` : ''}
            </span>
          </Link>
        ))}
      </Card>

      <SectionTitle>Bonus ({bonus.length})</SectionTitle>
      <Card>
        {bonus.length === 0 && (
          <p className="py-2 text-sm text-[var(--color-muted)]">Todavía no hay ninguna.</p>
        )}
        {bonus.map((task) => (
          <Link
            key={task.id}
            to={`/tareas/${task.id}`}
            className="flex items-center justify-between border-b border-[var(--color-line)] py-3 last:border-b-0"
          >
            <div className="min-w-0">
              <p className="text-[15px] break-words">{task.title}</p>
              <p className="text-xs text-[var(--color-muted)]">{describe(task)}</p>
            </div>
            <span className="shrink-0 text-sm text-[var(--color-good)]">
              +{eur(task.bonusAmountCents ?? 0)}
            </span>
          </Link>
        ))}
      </Card>
    </Screen>
  )
}

type Draft = {
  title: string
  kind: 'DEDUCTIBLE' | 'BONUS'
  assignmentMode: 'FIXED' | 'ROTATING' | 'OPEN'
  assigneeIds: string[]
  rotationMembers: string[]
  rotationPeriod: 'WEEKLY' | 'BY_OCCURRENCE'
  rotationAnchorDate: string | null
  openCandidates: string[]
  recurrence: TaskDefinition['recurrence']
  daysOfWeek: number[]
  oneOffDate: string | null
  dueTime: string | null
  weight: number
  bonusAmountCents: number | null
  requiresPhoto: boolean
  requiresApproval: boolean
}

function emptyDraft(): Draft {
  return {
    title: '',
    kind: 'DEDUCTIBLE',
    assignmentMode: 'FIXED',
    assigneeIds: [],
    rotationMembers: [],
    rotationPeriod: 'WEEKLY',
    rotationAnchorDate: todayIso(),
    openCandidates: [],
    recurrence: 'DAILY',
    daysOfWeek: [],
    oneOffDate: null,
    dueTime: null,
    weight: 1,
    bonusAmountCents: 300,
    requiresPhoto: false,
    requiresApproval: true,
  }
}

const inputClass =
  'w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 py-2 text-[16px]'

const RECURRENCES = [
  { value: 'DAILY', label: 'Diaria' },
  { value: 'WEEKLY', label: 'Semanal' },
  { value: 'WEEKLY_ANY', label: 'Cualquier día' },
  { value: 'ONE_OFF', label: 'Puntual' },
  { value: 'ON_DEMAND', label: 'A demanda' },
] as const satisfies readonly { value: Draft['recurrence']; label: string }[]

/** El nombre de la opción no basta para elegir bien: cada una dice qué hace. */
const RECURRENCE_HINTS: Record<Draft['recurrence'], string> = {
  DAILY: 'Todos los días, o solo los días que marques.',
  WEEKLY: 'Una vez por semana, el día que marques.',
  WEEKLY_ANY: 'Una vez por semana, el día que quieran. Desaparece en cuanto se hace.',
  ONE_OFF: 'Una sola vez, en la fecha que elijas.',
  ON_DEMAND: 'No aparece sola: la activas tú desde Inicio el día que haga falta.',
}

export function TaskEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'nueva'

  const tasks = useTasks()
  const users = useUsers()
  const settings = useSettings()
  const save = useSaveTask()
  const archive = useArchiveTask()
  const activate = useActivateTask()

  const existing = isNew ? null : (tasks.data?.find((t) => t.id === id) ?? null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [activating, setActivating] = useState(false)
  const [activated, setActivated] = useState<string | null>(null)

  const current = draft ?? (existing ? fromTask(existing) : isNew ? emptyDraft() : null)
  const children = (users.data ?? []).filter((u) => u.role === 'CHILD')

  const rotationPreview = useMemo(() => {
    if (!current || current.assignmentMode !== 'ROTATING' || current.rotationMembers.length < 2) {
      return []
    }
    const weekStartDay = (settings.data?.weekStartDay ?? 5) as IsoWeekday
    // Se calcula en el cliente con la MISMA funcion que usa el servidor al
    // materializar la semana, asi que la vista previa funciona antes de guardar
    // y no puede desviarse de lo que hara el backend.
    return previewRotation(
      {
        rotationMembers: current.rotationMembers,
        rotationPeriod: current.rotationPeriod,
        rotationAnchorDate: current.rotationAnchorDate ?? todayIso(),
      },
      weekStartFor(todayIso(), weekStartDay),
      weekStartDay,
      4,
    )
  }, [current, settings.data])

  if (tasks.isLoading || settings.isLoading) return <Spinner />
  if (!current) return <Screen><ErrorNote error={new Error('Tarea no encontrada')} /></Screen>

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft({ ...current, [key]: value })

  const toggleDay = (day: number) =>
    set(
      'daysOfWeek',
      current.daysOfWeek.includes(day)
        ? current.daysOfWeek.filter((d) => d !== day)
        : [...current.daysOfWeek, day].sort((a, b) => a - b),
    )

  const toggleAssignee = (userId: string) =>
    set(
      'assigneeIds',
      current.assigneeIds.includes(userId)
        ? current.assigneeIds.filter((u) => u !== userId)
        : [...current.assigneeIds, userId],
    )

  const toggleMember = (userId: string) =>
    set(
      'rotationMembers',
      current.rotationMembers.includes(userId)
        ? current.rotationMembers.filter((u) => u !== userId)
        : [...current.rotationMembers, userId],
    )

  const toggleCandidate = (userId: string) =>
    set(
      'openCandidates',
      current.openCandidates.includes(userId)
        ? current.openCandidates.filter((u) => u !== userId)
        : [...current.openCandidates, userId],
    )

  // Se valida con la misma funcion que usa el servidor, asi que si aqui no hay
  // problemas el guardado no puede fallar por las reglas del dominio.
  const payload = toPayload(current)
  const problems = payload.title
    ? validateDefinition(toDomain(payload))
    : ['La tarea necesita un título']

  async function submit() {
    await save.mutateAsync({ id: isNew ? undefined : id, body: payload })
    navigate('/tareas')
  }

  return (
    <Screen>
      <button onClick={() => navigate(-1)} className="mb-3 text-sm text-[var(--color-brand)]">
        ← Volver
      </button>
      <h1 className="mb-4 text-xl font-bold tracking-tight">
        {isNew ? 'Nueva tarea' : current.title}
      </h1>

      {save.error != null && <ErrorNote error={save.error} />}

      <Card className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">Título</span>
          <input
            className={inputClass}
            value={current.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Sacar la basura"
          />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">Tipo</span>
          <div className="flex gap-2">
            {(['DEDUCTIBLE', 'BONUS'] as const).map((kind) => (
              <button
                key={kind}
                onClick={() => {
                  const next = { ...current, kind }
                  // Un bonus abierto es valido; una obligatoria abierta no lo es.
                  if (kind === 'DEDUCTIBLE' && next.assignmentMode === 'OPEN') {
                    next.assignmentMode = 'FIXED'
                  }
                  setDraft(next)
                }}
                className={`min-h-11 flex-1 rounded-xl border px-3 text-sm font-medium ${
                  current.kind === kind
                    ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                    : 'border-[var(--color-line)]'
                }`}
              >
                {kind === 'DEDUCTIBLE' ? 'Obligatoria (resta)' : 'Bonus (suma)'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">Asignación</span>
          <div className="flex gap-2">
            {(['FIXED', 'ROTATING', 'OPEN'] as const)
              .filter((mode) => mode !== 'OPEN' || current.kind === 'BONUS')
              .map((mode) => (
                <button
                  key={mode}
                  onClick={() => set('assignmentMode', mode)}
                  className={`min-h-11 flex-1 rounded-xl border px-2 text-sm font-medium ${
                    current.assignmentMode === mode
                      ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                      : 'border-[var(--color-line)]'
                  }`}
                >
                  {mode === 'FIXED' ? 'Fija' : mode === 'ROTATING' ? 'Rotativa' : 'Abierta'}
                </button>
              ))}
          </div>
        </div>

        {current.assignmentMode === 'FIXED' && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">
              Responsables (puedes elegir varios)
            </span>
            <div className="flex flex-wrap gap-2">
              {children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => toggleAssignee(c.id)}
                  className={`min-h-11 rounded-xl border px-3 text-sm ${
                    current.assigneeIds.includes(c.id)
                      ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                      : 'border-[var(--color-line)]'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            {current.assigneeIds.length > 1 && (
              <span className="text-xs text-[var(--color-muted)]">
                Cada uno tendrá su propia copia de la tarea y responde de la suya.
              </span>
            )}
          </div>
        )}

        {current.assignmentMode === 'ROTATING' && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-[var(--color-muted)]">
                Turno (el orden importa)
              </span>
              <div className="flex flex-wrap gap-2">
                {children.map((c) => {
                  const index = current.rotationMembers.indexOf(c.id)
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleMember(c.id)}
                      className={`min-h-11 rounded-xl border px-3 text-sm ${
                        index >= 0
                          ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                          : 'border-[var(--color-line)]'
                      }`}
                    >
                      {index >= 0 ? `${index + 1}. ` : ''}
                      {c.name}
                    </button>
                  )
                })}
              </div>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-[var(--color-muted)]">Inicio del ciclo</span>
              <input
                type="date"
                className={inputClass}
                value={current.rotationAnchorDate ?? ''}
                onChange={(e) => set('rotationAnchorDate', e.target.value || null)}
              />
            </label>

            {/* La aritmetica modular del turno es imposible de predecir a ojo;
                ver los proximos turnos evita configurar mal el ancla. */}
            {rotationPreview.length > 0 && (
              <div className="rounded-xl bg-[var(--color-canvas)] p-3 text-sm">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                  Próximos turnos
                </p>
                {rotationPreview.map((turn) => (
                  <p key={turn.weekStart} className="text-[var(--color-muted)]">
                    {shortDate(turn.weekStart)} ·{' '}
                    <span className="text-[var(--color-ink)]">
                      {children.find((c) => c.id === turn.userId)?.name ?? '?'}
                    </span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {current.assignmentMode === 'OPEN' && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">
              Quién puede reclamarla (se la lleva el primero)
            </span>
            <div className="flex flex-wrap gap-2">
              {children.map((c) => (
                <button
                  key={c.id}
                  onClick={() => toggleCandidate(c.id)}
                  className={`min-h-11 rounded-xl border px-3 text-sm ${
                    current.openCandidates.includes(c.id)
                      ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                      : 'border-[var(--color-line)]'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">Repetición</span>
          <div className="grid grid-cols-3 gap-2">
            {RECURRENCES.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => set('recurrence', value)}
                className={`min-h-11 rounded-xl border px-2 text-sm font-medium ${
                  current.recurrence === value
                    ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                    : 'border-[var(--color-line)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-xs text-[var(--color-muted)]">
            {RECURRENCE_HINTS[current.recurrence]}
          </span>
        </div>

        {(current.recurrence === 'DAILY' || current.recurrence === 'WEEKLY') && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">
              {current.recurrence === 'WEEKLY' ? 'Día (solo uno)' : 'Días (ninguno = todos)'}
            </span>
            <div className="flex gap-1">
              {DAY_INITIALS.map((initial, index) => {
                const day = index + 1
                const on = current.daysOfWeek.includes(day)
                return (
                  <button
                    key={day}
                    onClick={() =>
                      current.recurrence === 'WEEKLY' ? set('daysOfWeek', [day]) : toggleDay(day)
                    }
                    className={`h-11 flex-1 rounded-lg border text-sm font-medium ${
                      on
                        ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                        : 'border-[var(--color-line)]'
                    }`}
                  >
                    {initial}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {current.recurrence === 'ONE_OFF' && (
          <label className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">Fecha</span>
            <input
              type="date"
              className={inputClass}
              value={current.oneOffDate ?? ''}
              onChange={(e) => set('oneOffDate', e.target.value || null)}
            />
          </label>
        )}

        {current.kind === 'DEDUCTIBLE' ? (
          <label className="flex flex-col gap-1">
            <span className="text-sm text-[var(--color-muted)]">Peso</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={20}
              className={inputClass}
              value={current.weight}
              onChange={(e) => set('weight', Number(e.target.value) || 1)}
            />
            <span className="text-xs text-[var(--color-muted)]">
              Déjalo en 1 salvo que cueste bastante más que el resto. Con peso 3 vale el triple.
            </span>
          </label>
        ) : (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-sm text-[var(--color-muted)]">Importe del bonus (€)</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className={inputClass}
                value={((current.bonusAmountCents ?? 0) / 100).toFixed(2)}
                onChange={(e) =>
                  set('bonusAmountCents', Math.round(Number(e.target.value) * 100) || 0)
                }
              />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-[15px]">Requiere aprobación</span>
              <input
                type="checkbox"
                className="h-6 w-6"
                checked={current.requiresApproval}
                onChange={(e) => set('requiresApproval', e.target.checked)}
              />
            </label>
          </>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-sm text-[var(--color-muted)]">Hora límite (opcional)</span>
          <input
            type="time"
            className={inputClass}
            value={current.dueTime ?? ''}
            onChange={(e) => set('dueTime', e.target.value || null)}
          />
        </label>

        {/* Sólo en los bonus: una tarea obligatoria se presume hecha y nadie la
            marca, así que no hay ningún momento en el que se entregue la foto. */}
        {current.kind === 'BONUS' && (
          <label className="flex items-center justify-between">
            <span className="text-[15px]">Pedir foto para aprobarla</span>
            <input
              type="checkbox"
              className="h-6 w-6"
              checked={current.requiresPhoto}
              onChange={(e) => set('requiresPhoto', e.target.checked)}
            />
          </label>
        )}
      </Card>

      {problems.length > 0 && (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-[var(--color-bad)]">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <Button
        className="mt-5 w-full"
        disabled={save.isPending || problems.length > 0}
        onClick={() => void submit()}
      >
        {save.isPending ? 'Guardando…' : 'Guardar'}
      </Button>

      {/* Una tarea recién creada no existe hasta el viernes que viene, cuando se
          materializa la semana. Esto la pone hoy —o la aplica ya como castigo—
          sin esperar al cambio de semana. */}
      {!isNew && existing && (
        <>
          <Button
            variant="secondary"
            className="mt-3 w-full"
            disabled={activate.isPending}
            onClick={() => setActivating(true)}
          >
            {existing.kind === 'BONUS' ? 'Ofrecerla hoy' : 'Ponerla hoy o descontarla'}
          </Button>
          {activate.error != null && <div className="mt-2"><ErrorNote error={activate.error} /></div>}
          {activated != null && (
            <p className="mt-2 text-center text-sm text-[var(--color-good)]">{activated}</p>
          )}
        </>
      )}

      {!isNew && (
        <Button
          variant="danger"
          className="mt-3 w-full"
          disabled={archive.isPending}
          onClick={async () => {
            if (!window.confirm('¿Archivar esta tarea? Las instancias ya creadas se conservan.'))
              return
            await archive.mutateAsync(id!)
            navigate('/tareas')
          }}
        >
          Archivar
        </Button>
      )}

      <ActivateDialog
        open={activating && existing !== null}
        task={existing}
        candidates={children}
        busy={activate.isPending}
        onCancel={() => setActivating(false)}
        onConfirm={(activation) =>
          activate.mutate(
            { id: id!, body: activation },
            {
              onSuccess: (result) => {
                setActivating(false)
                setActivated(
                  result.deductedCents > 0
                    ? `Descontado: ${eur(result.deductedCents)} a ${result.assignees.join(' y ')}`
                    : `Puesta hoy a ${result.assignees.join(' y ')}`,
                )
              },
            },
          )
        }
      />
    </Screen>
  )
}

/**
 * Cuerpo que espera la API, ya sin los campos que no aplican a este tipo de
 * tarea. El formulario conserva lo que escribiste al cambiar de tipo o de
 * asignación (para no perderlo si vuelves), pero eso no debe llegar al
 * servidor: un importe de bonus en una tarea obligatoria lo haría fallar.
 */
function toPayload(draft: Draft) {
  const bonus = draft.kind === 'BONUS'
  return {
    title: draft.title.trim(),
    kind: draft.kind,
    assignmentMode: draft.assignmentMode,
    assigneeIds: draft.assignmentMode === 'FIXED' ? draft.assigneeIds : [],
    rotationMembers: draft.assignmentMode === 'ROTATING' ? draft.rotationMembers : [],
    rotationPeriod: draft.rotationPeriod,
    rotationAnchorDate: draft.assignmentMode === 'ROTATING' ? draft.rotationAnchorDate : null,
    openCandidates: draft.assignmentMode === 'OPEN' ? draft.openCandidates : [],
    recurrence: draft.recurrence,
    // Solo la diaria y la semanal se atan a dias concretos; las demas los
    // ignoran y mandarlos seria un error de validacion.
    daysOfWeek:
      draft.recurrence === 'DAILY' || draft.recurrence === 'WEEKLY' ? draft.daysOfWeek : [],
    oneOffDate: draft.recurrence === 'ONE_OFF' ? draft.oneOffDate : null,
    dueTime: draft.dueTime,
    weight: bonus ? 1 : draft.weight,
    bonusAmountCents: bonus ? draft.bonusAmountCents : null,
    requiresPhoto: bonus ? draft.requiresPhoto : false,
    requiresApproval: draft.requiresApproval,
  }
}

function toDomain(body: ReturnType<typeof toPayload>): TaskDefinitionInput {
  return {
    id: 'nueva',
    kind: body.kind,
    assignmentMode: body.assignmentMode,
    assigneeIds: body.assigneeIds,
    rotationMembers: body.rotationMembers,
    rotationPeriod: body.rotationPeriod,
    rotationAnchorDate: body.rotationAnchorDate ?? undefined,
    openCandidates: body.openCandidates,
    recurrence: body.recurrence,
    daysOfWeek: body.daysOfWeek as IsoWeekday[],
    oneOffDate: body.oneOffDate,
    weight: body.kind === 'DEDUCTIBLE' ? body.weight : undefined,
    bonusAmountCents: body.kind === 'BONUS' ? (body.bonusAmountCents ?? undefined) : undefined,
  }
}

function fromTask(task: TaskDefinition): Draft {
  return {
    title: task.title,
    kind: task.kind,
    assignmentMode: task.assignmentMode,
    assigneeIds: task.assignees.map((a) => a.userId),
    rotationMembers: [...task.rotationMembers]
      .sort((a, b) => a.position - b.position)
      .map((m) => m.userId),
    rotationPeriod: task.rotationPeriod ?? 'WEEKLY',
    rotationAnchorDate: task.rotationAnchorDate,
    openCandidates: task.openCandidates.map((c) => c.userId),
    recurrence: task.recurrence,
    daysOfWeek: task.daysOfWeek,
    oneOffDate: task.oneOffDate,
    dueTime: task.dueTime,
    weight: task.weight,
    bonusAmountCents: task.bonusAmountCents,
    requiresPhoto: task.requiresPhoto,
    requiresApproval: task.requiresApproval,
  }
}
